import { useState, useEffect } from 'react';
import {
  FileText, ExternalLink, RefreshCw, X, Check,
  Pencil, AlertTriangle, Upload, FolderSync,
} from 'lucide-react';
import { documentsApi, documentTypesApi } from '../api';
import type { Document, DocumentType } from '../types';
import { useSettings } from '../context/SettingsContext';
import { ConfidenceBar, Initials, TYPE_COLORS, STATUS_CONFIG } from './ui';
import { ExtractedFieldsEditor } from './ExtractedFieldsEditor';
import { timeAgo } from '../utils';

// ── Source badge (réplique locale du Dashboard) ───────────────────

function SourceBadge({ source }: { source?: string }) {
  if (source?.startsWith('scan:')) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-violet-50 text-violet-700 border border-violet-100">
        <FolderSync className="w-3 h-3" /> {source.slice(5)}
      </span>
    );
  }
  if (source === 'api') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-sky-50 text-sky-700 border border-sky-100">
        API
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-50 text-gray-500 border border-gray-100">
      <Upload className="w-3 h-3" /> Import
    </span>
  );
}

// ── Composant principal ───────────────────────────────────────────

interface Props {
  docId: string;
  /** Label du bouton de fermeture (ex: "← Retour" ou icône X) */
  onClose: () => void;
  /** Rappelé après chaque mutation pour que le parent rafraîchisse ses listes */
  onUpdated?: () => void;
}

export function DocumentDetailPanel({ docId, onClose, onUpdated }: Props) {
  const { formatDate, settings } = useSettings();
  const [doc, setDoc]           = useState<Document | null>(null);
  const [loading, setLoading]   = useState(true);
  const [docTypes, setDocTypes] = useState<DocumentType[]>([]);
  const [changingType, setChangingType] = useState(false);
  const [newTypeId, setNewTypeId]       = useState('');
  const [savingType, setSavingType]     = useState(false);
  const [validating, setValidating]     = useState(false);
  const [justValidated, setJustValidated] = useState(false);
  const [reprocessing, setReprocessing] = useState(false);

  const load = () => {
    setLoading(true);
    documentsApi.get(docId)
      .then(r => setDoc(r.data))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    setDoc(null);
    setChangingType(false);
    setNewTypeId('');
    setJustValidated(false);
    load();
    documentTypesApi.list().then(r => setDocTypes(r.data)).catch(() => {});
  }, [docId]);

  const handleSaveType = async () => {
    if (!newTypeId) return;
    setSavingType(true);
    try {
      await documentsApi.update(docId, { document_type_id: newTypeId });
      setChangingType(false);
      setNewTypeId('');
      load();
      onUpdated?.();
    } finally {
      setSavingType(false);
    }
  };

  const handleValidate = async () => {
    setValidating(true);
    try {
      await documentsApi.validate(docId);
      setJustValidated(true);
      load();
      onUpdated?.();
    } finally {
      setValidating(false);
    }
  };

  const handleReprocess = async () => {
    setReprocessing(true);
    try {
      await documentsApi.reprocess(docId);
      load();
      onUpdated?.();
    } finally {
      setReprocessing(false);
    }
  };

  const handlePreview = async () => {
    try {
      const res = await documentsApi.getPreviewUrl(docId);
      const blob = new Blob([res.data], { type: res.headers['content-type'] as string | undefined });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch {
      alert("Impossible d'ouvrir le document.");
    }
  };

  // ── Header commun (toujours affiché par le parent) ───────────────
  // Le header avec le bouton fermeture est géré dans le parent pour
  // permettre le label contextuel ("← Retour" ou "Détail document").

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <RefreshCw className="w-6 h-6 text-gray-400 animate-spin" />
      </div>
    );
  }

  if (!doc) return null;

  const typeCode  = doc.document_type?.code  ?? 'autre';
  const typeLabel = doc.document_type?.label ?? 'Autre';
  const tc = TYPE_COLORS[typeCode] || TYPE_COLORS.autre;
  const sc = STATUS_CONFIG[doc.status] || STATUS_CONFIG.en_cours;

  const ocrThreshold = settings?.ocr_threshold ?? 80;
  const ocrQualityPct = doc.ocr_confidence != null ? Math.round(doc.ocr_confidence * 100) : null;
  const lowQuality = ocrQualityPct != null && ocrQualityPct < ocrThreshold;

  return (
    <>
      {/* Aperçu */}
      <div className={`mx-5 mt-5 h-40 ${tc.bg} rounded-xl border border-gray-100 flex flex-col items-center justify-center gap-3`}>
        <FileText className={`w-12 h-12 ${tc.text} opacity-50`} />
        <button
          onClick={handlePreview}
          className={`flex items-center gap-1.5 text-xs font-semibold ${tc.text} hover:underline`}
        >
          <ExternalLink className="w-3.5 h-3.5" /> Ouvrir le document
        </button>
      </div>

      {/* Qualité image insuffisante */}
      {lowQuality && (
        <div className="mx-5 mt-4 bg-red-50 border border-red-200 rounded-xl p-4">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-5 h-5 text-red-500 mt-0.5 shrink-0" />
            <div>
              <p className="text-sm font-bold text-red-800">
                Qualité d'image insuffisante ({ocrQualityPct}%)
              </p>
              <p className="text-xs text-red-700 mt-1">
                L'image est trop floue pour une lecture fiable (seuil&nbsp;: {ocrThreshold}%).
                Merci de fournir un scan plus net, puis de retraiter le document.
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="p-5 space-y-5">
        {/* Nom + type + statut */}
        <div>
          <p className="text-sm font-bold text-gray-900 leading-snug">{doc.original_filename}</p>
          <p className="text-xs text-gray-400 mt-0.5">{timeAgo(doc.created_at)}</p>
          <div className="flex items-center gap-2 mt-2 flex-wrap">
            {changingType ? (
              <div className="flex items-center gap-1 flex-wrap">
                <select
                  value={newTypeId}
                  onChange={e => setNewTypeId(e.target.value)}
                  className="border border-gray-200 rounded-lg px-2 py-1 text-xs bg-white"
                >
                  <option value="">— Choisir —</option>
                  {docTypes.map(dt => (
                    <option key={dt.id} value={dt.id}>{dt.label}</option>
                  ))}
                </select>
                <button
                  onClick={handleSaveType}
                  disabled={!newTypeId || savingType}
                  className="p-1 bg-teal-600 hover:bg-teal-700 disabled:opacity-50 text-white rounded-lg"
                >
                  <Check className="w-3.5 h-3.5" />
                </button>
                <button
                  onClick={() => { setChangingType(false); setNewTypeId(''); }}
                  className="p-1 hover:bg-gray-100 rounded-lg"
                >
                  <X className="w-3.5 h-3.5 text-gray-400" />
                </button>
              </div>
            ) : (
              <button
                onClick={() => { setChangingType(true); setNewTypeId(doc.document_type?.id ?? ''); }}
                className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold ${tc.badge} hover:opacity-80`}
              >
                {typeLabel} <Pencil className="w-3 h-3 ml-0.5" />
              </button>
            )}
            <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold ${sc.color}`}>
              {sc.label}
            </span>
            <SourceBadge source={doc.source} />
          </div>
          {doc.metadata_extra?.sous_type && (
            <p className="text-xs text-gray-500 mt-1.5 italic">
              {doc.metadata_extra.sous_type}
            </p>
          )}
        </div>

        {/* Collaborateur */}
        {doc.collaborateur && (
          <div className="flex items-center gap-3 bg-gray-50 rounded-xl p-3">
            <Initials nom_complet={doc.collaborateur.nom_complet} size="md" />
            <div>
              <p className="text-xs text-gray-500">Collaborateur</p>
              <p className="text-sm font-semibold text-gray-900">{doc.collaborateur.nom_complet}</p>
            </div>
          </div>
        )}

        {/* Fiabilité */}
        {doc.classification_confidence != null && (
          <div>
            <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">Fiabilité de la détection</p>
            <ConfidenceBar value={doc.classification_confidence} />
          </div>
        )}

        {/* Champs extraits — éditables */}
        <div>
          <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Informations extraites</p>
          <ExtractedFieldsEditor
            docId={doc.id}
            extractedData={doc.extracted_data ?? []}
            onUpdated={() => { load(); onUpdated?.(); }}
          />
        </div>

        {/* Valider */}
        {(doc.status === 'a_verifier' || doc.status === 'traite') && (
          <button
            onClick={handleValidate}
            disabled={validating || justValidated}
            className={`w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold transition text-white ${
              justValidated
                ? 'bg-emerald-600 cursor-default'
                : 'bg-teal-600 hover:bg-teal-700 disabled:bg-teal-400'
            }`}
          >
            {validating
              ? <><RefreshCw className="w-4 h-4 animate-spin" /> Validation...</>
              : justValidated
                ? <><Check className="w-4 h-4" /> Document validé</>
                : <><Check className="w-4 h-4" /> Valider le document</>}
          </button>
        )}

        {/* Retraiter */}
        <button
          onClick={handleReprocess}
          disabled={reprocessing}
          className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl border border-gray-200 text-sm font-semibold text-gray-600 hover:bg-gray-50 disabled:opacity-50 transition"
        >
          <RefreshCw className={`w-4 h-4 ${reprocessing ? 'animate-spin' : ''}`} />
          {reprocessing ? 'Retraitement...' : 'Retraiter le document'}
        </button>
      </div>

      {/* Alerte RGPD */}
      {doc.date_expiration && (
        <div className="mx-5 mb-5 bg-orange-50 border border-orange-200 rounded-xl p-4">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-5 h-5 text-orange-500 mt-0.5 shrink-0" />
            <div>
              <p className="text-sm font-bold text-orange-800">Alerte RGPD</p>
              <p className="text-xs text-orange-700 mt-1">
                Expiration prévue le {formatDate(doc.date_expiration)}.
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
